/**
 * Professional audit document numbers.
 * Format: PREFIX-YYYY-000001 (stable, sequential from DB id + year of the record).
 *
 * ORD — POS / customer orders
 * RCP — customer payment receipts
 * FPY — farmer (vendor) payments
 * ST  — farmer settlement / clear statements
 */

const pad = (id, width = 6) => String(Math.max(0, Math.trunc(Number(id) || 0))).padStart(width, "0");

const yearFrom = (dateLike) => {
  if (!dateLike) return new Date().getFullYear();
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return Number.isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
};

export const DOC = {
  ORDER: "ORD",
  CUSTOMER_PAYMENT: "RCP",
  FARMER_PAYMENT: "FPY",
  SETTLEMENT: "ST",
};

/**
 * @param {"ORD"|"RCP"|"FPY"|"ST"|string} prefix
 * @param {number|string} id
 * @param {string|Date|null} [date]
 * @returns {string}
 */
export function formatDocumentNo(prefix, id, date = null) {
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${prefix}-${yearFrom(date)}-${pad(n)}`;
}

export const formatOrderNo = (id, date) => formatDocumentNo(DOC.ORDER, id, date);
export const formatCustomerPaymentNo = (id, date) => formatDocumentNo(DOC.CUSTOMER_PAYMENT, id, date);
export const formatFarmerPaymentNo = (id, date) => formatDocumentNo(DOC.FARMER_PAYMENT, id, date);
export const formatSettlementNo = (id, date) => formatDocumentNo(DOC.SETTLEMENT, id, date);

/**
 * Prefer API `document_no` / `order_document_no`; otherwise compute ORD-YYYY-######.
 * Pass a row object, or (id, date).
 */
export function orderDocNo(rowOrId, date = null) {
  if (rowOrId != null && typeof rowOrId === "object") {
    const row = rowOrId;
    if (row.document_no) return String(row.document_no);
    if (row.order_document_no) return String(row.order_document_no);
    const id = row.order_id ?? row.id;
    return formatOrderNo(id, row.order_created_at || row.order_date || row.created_at || date);
  }
  return formatOrderNo(rowOrId, date);
}

export function paymentDocNo(rowOrId, date = null) {
  if (rowOrId != null && typeof rowOrId === "object") {
    const row = rowOrId;
    if (row.entry_type && row.entry_type !== "payment") {
      return row.document_no || "—";
    }
    if (row.document_no) return String(row.document_no);
    const id = row.payment_id ?? (typeof row.id === "number" ? row.id : null);
    if (id == null) return "—";
    return formatCustomerPaymentNo(id, row.created_at || date);
  }
  return formatCustomerPaymentNo(rowOrId, date);
}

/** Live (not yet archived) farmer statement preview number */
export function formatLiveStatementNo(vendorId, date = null) {
  const d = date ? (date instanceof Date ? date : new Date(date)) : new Date();
  const y = Number.isNaN(d.getTime()) ? new Date() : d;
  const stamp = [
    String(y.getFullYear()),
    String(y.getMonth() + 1).padStart(2, "0"),
    String(y.getDate()).padStart(2, "0"),
    "-",
    String(y.getHours()).padStart(2, "0"),
    String(y.getMinutes()).padStart(2, "0"),
  ].join("");
  return `ST-V${pad(vendorId, 4)}-${stamp}`;
}
